import { useEffect, useRef, useState } from "react";
import { PIN_DIGITS, pinDigits, type PinMode } from "../../../../shared/pins";
import type { Answer } from "../../../../shared/protocol";
import "./Pin.css";

/** "14:07" on this phone's clock. */
export function clock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** A name locked after five wrong tries, in words. */
export function lockedWords(until: number): string {
  return `Too many wrong tries. Try again at ${clock(until)}.`;
}

/** Four digits, on the number pad, shown as dots. `onDone` gets the four digits as they land. */
function PinField({ label, value, onChange, autoFocus, onDone }: {
  label: string;
  value: string;
  onChange(next: string): void;
  autoFocus?: boolean;
  onDone?(pin: string): void;
}) {
  return (
    <label className="pin-field">
      <span>{label}</span>
      <input
        className="pin-input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="done"
        maxLength={PIN_DIGITS}
        value={value}
        autoFocus={autoFocus}
        aria-label={label}
        onChange={(e) => {
          const next = pinDigits(e.target.value);
          onChange(next);
          if (next.length === PIN_DIGITS) onDone?.(next);
        }}
      />
    </label>
  );
}

/**
 * Setting a PIN (typed twice) or typing one. `send` goes to the server and comes back with its
 * answer; a wrong one clears the field and says why, a locked name says until when. It sends by
 * itself once the PIN is whole (and, when setting, the second matches the first): an iPhone's
 * number pad has no Go key, and the button can sit under the pad. The button stays for the rest.
 */
export function PinEntry({ mode, send, yes, lockedUntil = null, onAnswer, className = "" }: {
  mode: PinMode;
  send(pin: string): Promise<Answer>;
  /** The button's word: "Set my PIN", "Open". */
  yes: string;
  /** Locked when the step opened. */
  lockedUntil?: number | null;
  /** Every answer, for a step that changes on it (a PIN set from another phone meanwhile). */
  onAnswer?(a: Answer): void;
  className?: string;
}) {
  const [first, setFirst] = useState("");
  const [again, setAgain] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [until, setUntil] = useState<number | null>(lockedUntil);
  const againRef = useRef<HTMLDivElement | null>(null);
  const [, tick] = useState(0);

  // a lock runs out by itself: the words go when it does
  useEffect(() => {
    if (until === null) return;
    const left = until - Date.now();
    if (left <= 0) {
      setUntil(null);
      return;
    }
    const timer = setTimeout(() => tick((n) => n + 1), Math.min(left + 50, 30_000));
    return () => clearTimeout(timer);
  });
  useEffect(() => setUntil(lockedUntil), [lockedUntil]);

  const locked = until !== null && until > Date.now();
  const whole = first.length === PIN_DIGITS && (mode === "enter" || again.length === PIN_DIGITS);
  const mismatch = mode === "set" && again.length === PIN_DIGITS && again !== first;
  // one send at a time, even when the last digit and the keyboard's own key land together
  const busy = useRef(false);

  /** Sends the PIN; `f` and `g` are the two fields as just typed, before the screen catches up. */
  async function go(f: string = first, g: string = again) {
    const complete = f.length === PIN_DIGITS && (mode === "enter" || g.length === PIN_DIGITS);
    if (!complete || busy.current || locked) return;
    if (mode === "set" && g !== f) {
      setProblem("The two PINs are not the same. Type them again.");
      setFirst("");
      setAgain("");
      return;
    }
    busy.current = true;
    setSending(true);
    setProblem(null);
    const a = await send(f);
    busy.current = false;
    setSending(false);
    onAnswer?.(a);
    if (a.ok) return;
    setFirst("");
    setAgain("");
    if (a.lockedUntil && a.lockedUntil > Date.now()) setUntil(a.lockedUntil);
    setProblem(a.lockedUntil && a.lockedUntil > Date.now() ? null : a.reason ?? "That did not work. Try again.");
  }

  return (
    <form className={"pin-entry " + className} onSubmit={(e) => { e.preventDefault(); void go(); }}>
      {locked && until !== null ? (
        <p className="pin-problem" role="alert">{lockedWords(until)}</p>
      ) : (
        <>
          <PinField label={mode === "set" ? "Four numbers" : "PIN"} value={first} autoFocus
            onChange={(v) => { setFirst(v); setProblem(null); }}
            onDone={(v) => {
              if (mode === "enter") void go(v);
              else if (again.length === PIN_DIGITS && again === v) void go(v, again);
              else againRef.current?.querySelector("input")?.focus();
            }} />
          {mode === "set" && (
            <div ref={againRef}>
              {/* sends once it matches; a mismatch only says so, and the first PIN stays */}
              <PinField label="The same again" value={again} onChange={(v) => { setAgain(v); setProblem(null); }}
                onDone={(v) => { if (v === first) void go(first, v); }} />
            </div>
          )}
          {(problem || mismatch) && (
            <p className="pin-problem" role="alert">{problem ?? "The two PINs are not the same."}</p>
          )}
          <button type="submit" className="btn accent pin-go" disabled={!whole || sending || mismatch}>
            {sending ? "Checking" : yes}
          </button>
        </>
      )}
    </form>
  );
}
